import { dateFns } from 'iso-fns2'
import sum from 'lodash/sum'
import uniq from 'lodash/uniq'
import { BillingPeriod, BillingPeriodFns } from '../../internal'
import { modelFamilySystemView, PricingTierEnum } from '../../fleet-operations/model-family'
import { salesQuoteSeasonsModel } from './sales-quote-seasons-model'
import {
  Orv_Peak_Pricing_Matrix,
  Orv_Pricing_Matrix,
  Atv_Pricing_Matrix,
  Slg_Pricing_Matrix,
  Marine_Pricing_Matrix,
  Sno_PricingTier_Matrix
} from './sales-quote-season-matrices'
import { getFamilyPricingTierConfiguration } from './sales-quote-season-pricing-tier-configurations'
import { indexIntoMatrix, roundUSDToCents } from './sales-quote-season-helpers'

const IMX_CERTAIN_VEHICLE_COUNT = 8
const IMX_DEFAULT_PRICE_PER_VEHICLE = 4500
const SNO_DEFAULT_PRICE_PER_VEHICLE = 800
const SNO_PERFORMANCE_DEFAULT_PRICE_PER_VEHICLE = 600

export function getSalesQuoteSeasonBillingPeriods(
  season: Pick<salesQuoteSeasonsModel, 'activePeriods' | 'numberOfMonths' | 'contractedStartDate'>
) {
  const periods: BillingPeriod[] = []
  if (season.activePeriods.length > 0) {
    season.activePeriods.map((period) => {
      for (let month = 0; month < period.numberOfMonths; month++) {
        if (!dateFns.isValid(period.startDate)) {
          console.warn(
            `${period.startDate} - is invalid date value for the active period of the sales vehicle order holding term`
          )
          continue
        }
        const date = dateFns.add(period.startDate, { months: month })
        periods.push(BillingPeriodFns.fromIsoDate(date))
      }
    })
  } else {
    for (let month = 0; month < season.numberOfMonths; month++) {
      const date = dateFns.add(season.contractedStartDate, { months: month })
      periods.push(BillingPeriodFns.fromIsoDate(date))
    }
  }

  return uniq(periods)
}

export function getSalesQuoteSeasonVehicleCount(season: Pick<salesQuoteSeasonsModel, 'lineItems'>): number {
  const vehicleCount = sum(season.lineItems.map((l) => l.quantity))
  return vehicleCount
}

export function getSeasonMaxNumberOfActivePeriods(season: Pick<salesQuoteSeasonsModel, 'type'>) {
  switch (season.type) {
    case 'ATV':
      return 0
    case 'CUSTOM':
      return 0
    case 'IMX':
      return 1
    case 'ORV_PEAK':
      return 0
    case 'ORV':
      return 0
    case 'MARINE':
      return 4
    case 'SLG':
      return 4
    case 'SNO':
      return 0
    case 'SNO_PERFORMANCE':
      return 0
    default:
      throw new Error(`Unrecognized holding term type: ${season.type}`)
  }
}

export function getSeasonDefaultRideFee(season: Pick<salesQuoteSeasonsModel, 'type'>): number {
  switch (season.type) {
    case 'ATV':
      return 20
    case 'CUSTOM':
      return 20
    case 'IMX':
      return 20
    case 'ORV_PEAK':
      return 20
    case 'ORV':
      return 20
    case 'SLG':
      return 20
    case 'MARINE':
      return 0
    case 'SNO':
      return 10
    case 'SNO_PERFORMANCE':
      return 10
    default:
      throw new Error(`Unrecognized holding term type: ${season.type}`)
  }
}

export function getSeasonDefaultProgramFee(
  season: Pick<salesQuoteSeasonsModel, 'type' | 'lineItems' | 'activePeriods' | 'numberOfMonths' | 'contractedStartDate'>,
  modelFamilies: modelFamilySystemView[],
  extraProgramExpense: number
): number | null {
  const requiredContractDateSeasonTypes = ['ATV', 'IMX', 'ORV_PEAK', 'ORV', 'SLG', 'MARINE']
  if (requiredContractDateSeasonTypes.includes(season.type) && !season.contractedStartDate) {
    return null
  }

  let programFee: number | null = null
  switch (season.type) {
    case 'ATV': {
      programFee = getProgramFeeByPricingTier(season, modelFamilies, Atv_Pricing_Matrix)
      break
    }
    case 'CUSTOM': {
      programFee = null
      break
    }
    case 'IMX': {
      const billingPeriods = getSalesQuoteSeasonBillingPeriods(season)
      const vehicleCount = getSalesQuoteSeasonVehicleCount(season)

      programFee =
        vehicleCount === IMX_CERTAIN_VEHICLE_COUNT
          ? (IMX_DEFAULT_PRICE_PER_VEHICLE * IMX_CERTAIN_VEHICLE_COUNT) / billingPeriods.length
          : null
      break
    }
    case 'ORV_PEAK': {
      programFee = getProgramFeeByPricingTier(season, modelFamilies, Orv_Peak_Pricing_Matrix)
      break
    }
    case 'ORV': {
      programFee = getProgramFeeByPricingTier(season, modelFamilies, Orv_Pricing_Matrix)
      break
    }
    case 'SLG': {
      programFee = getProgramFeeByPricingTier(season, modelFamilies, Slg_Pricing_Matrix)
      break
    }
    case 'MARINE': {
      programFee = getProgramFeeByPricingTier(season, modelFamilies, Marine_Pricing_Matrix)
      break
    }
    case 'SNO': {
      programFee = getSNOProgramFee(season, modelFamilies, SNO_DEFAULT_PRICE_PER_VEHICLE)
      break
    }
    case 'SNO_PERFORMANCE': {
      const vehicleCount = getSalesQuoteSeasonVehicleCount(season)
      programFee = SNO_PERFORMANCE_DEFAULT_PRICE_PER_VEHICLE * vehicleCount
      break
    }
    default:
      throw new Error(`Unrecognized holding term type: ${season.type}`)
  }

  const extraProgramExpenseByNumberOfMonths = Math.round(extraProgramExpense / season.numberOfMonths)

  if (!programFee || season.type === 'ORV_PEAK') return programFee
  return programFee + extraProgramExpenseByNumberOfMonths
}

export function programFeePerVehicle(
  season: Pick<salesQuoteSeasonsModel, 'type' | 'lineItems' | 'activePeriods' | 'numberOfMonths' | 'contractedStartDate'>,
  pricingTier: PricingTierEnum
) {
  let pricingMatrix
  switch (season.type) {
    case 'ATV': {
      pricingMatrix = Atv_Pricing_Matrix
      break
    }
    case 'ORV_PEAK': {
      pricingMatrix = Orv_Peak_Pricing_Matrix
      break
    }
    case 'ORV': {
      pricingMatrix = Orv_Pricing_Matrix
      break
    }
    case 'SLG': {
      pricingMatrix = Slg_Pricing_Matrix
      break
    }
    case 'MARINE': {
      pricingMatrix = Marine_Pricing_Matrix
      break
    }
  }

  const billingPeriods = getSalesQuoteSeasonBillingPeriods(season)
  const colIndex = billingPeriods.length - 1
  const rowIndex = getSalesQuoteSeasonVehicleCount(season)

  const priceFromMatrix = indexIntoMatrix(pricingMatrix, rowIndex, colIndex)
  if (!priceFromMatrix) return priceFromMatrix

  const coreProgramFeePerVehicle = roundUSDToCents(priceFromMatrix / rowIndex)

  const pricingTierConfiguration = getFamilyPricingTierConfiguration(season.type)
  const pricingTierMultiplier = pricingTierConfiguration[pricingTier] || 1

  const result = coreProgramFeePerVehicle * pricingTierMultiplier

  return Math.round(result)
}

function getProgramFeeByPricingTier(
  season: Pick<salesQuoteSeasonsModel, 'type' | 'lineItems' | 'activePeriods' | 'numberOfMonths' | 'contractedStartDate'>,
  modelFamilies: modelFamilySystemView[],
  pricingMatrix: (number | null)[][]
): number | null {
  const billingPeriods = getSalesQuoteSeasonBillingPeriods(season)
  const colIndex = billingPeriods.length - 1
  const rowIndex = getSalesQuoteSeasonVehicleCount(season)

  const priceFromMatrix = indexIntoMatrix(pricingMatrix, rowIndex, colIndex)
  if (!priceFromMatrix) return priceFromMatrix

  const coreProgramFeePerVehicle = roundUSDToCents(priceFromMatrix / rowIndex)

  const result = sum(
    season.lineItems.map((l) => {
      const family = modelFamilies.find((m) => m.id === l.modelFamilyId)

      const pricingTierConfiguration = getFamilyPricingTierConfiguration(season.type)
      const familyPriceMultiplier = pricingTierConfiguration[family?.pricingTier as any] || 1

      const exactPerVehicleFee = roundUSDToCents(coreProgramFeePerVehicle * familyPriceMultiplier)
      return l.quantity * exactPerVehicleFee
    })
  )

  return Math.round(result)
}

function getSNOProgramFee(
  season: Pick<salesQuoteSeasonsModel, 'lineItems' | 'activePeriods' | 'numberOfMonths' | 'contractedStartDate'>,
  modelFamilies: modelFamilySystemView[],
  pricePerVehicle: number
): number {
  return sum(
    season.lineItems.map((l) => {
      const family = modelFamilies.find((m) => m.id === l.modelFamilyId)
      const pricingTierConfiguration = getFamilyPricingTierConfiguration('SNO')
      const pricingTierIndex = pricingTierConfiguration[family?.pricingTier as any] ?? -1
      const tierLine = Sno_PricingTier_Matrix[pricingTierIndex]
      if (tierLine) {
        const billingPeriods = getSalesQuoteSeasonBillingPeriods(season)
        const colIndex = billingPeriods.length - 1

        return l.quantity * (tierLine[colIndex] as number)
      } else {
        return l.quantity * pricePerVehicle
      }
    })
  )
}
